import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid2,
  TextField,
  Typography,
} from "@mui/material";
import CitySelect from "Components/Common/CitySelect";
import CountrySelect from "Components/Common/CountrySelect";
import GenderSelect from "Components/Common/GenderSelect";
import PhoneNumberInput from "Components/Common/PhoneNumberInput";
import PincodeSelect from "Components/Common/PincodeSelect";
import StateSelect from "Components/Common/StateSelect";
import TimezoneSelect from "Components/Common/TimezoneSelect";
import TShirtSizeSelect from "Components/Common/TShirtSizeSelect";
import { useFormik } from "formik";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile } from "Slices/thunks";
import * as yup from "yup";

const styles = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  border: `1px solid ${theme.palette.border.blue}`,
  borderRadius: "8px",
  padding: "16px",
});

const AccountDetails = ({ profileInfo, setSelectedTab = () => {} }) => {
  const dispatch = useDispatch();
  const [minMobileLength, setMinMobileLength] = useState();

  const { Translation, Common } = useSelector((state) => ({
    Common: state.Common.common || {},
    Translation: state.Translation.translation?.Settings || {},
  }));

  const Schema = useMemo(() => {
    return yup.object({
      FirstName: yup.string().required(Translation?.FirstNameRequired || "First name is required field."),
      LastName: yup.string().required(Translation?.LastNameRequired || "Last name is required field."),
      GenderID: yup.string().required(Translation?.GenderRequired || "Gender is required field."),
      CountryCodeID: yup.string().required(Translation?.DialCodeRequired || "Dial code is required field."),
      Mobile: yup.string().required(Translation?.PhoneNumberRequired || "Phone number is required field.")
      .min(minMobileLength, Translation?.ValidPhoneNumber?.replace('XXX', minMobileLength) || `Phone number should be at least ${minMobileLength} digits.`),
      Email: yup.string().required(Translation?.EmailRequired || "Email is required field."),
      Pincode: yup.string().required(Translation?.PincodeRequired || "Pincode is required field."),
      StreetAddress: yup.string().required(Translation?.StreetAddressRequired || "Street address is required field."),
      Locality: yup.string().required(Translation?.LocalityRequired || "Locality is required field."),
      Landmark: yup.string(),
      CityID: yup.string().required(Translation?.CityRequired || "City is required field."),
      StateID: yup.string().required(Translation?.StateRequired || "State is required field."),
      CountryID: yup.string().required(Translation?.CountryRequired || "Country is required field."),
      TimezoneID: yup.mixed().required(Translation?.TimezoneRequired || "Timezone is required field."),
      TShirtID: yup.mixed().required(Translation?.TShirtRequired || "T-Shirt Size is required field."),
      InterviewBitProfile: yup.string(),
      CodeForceProfile: yup.string(),
      CodeChefProfile: yup.string(),
      HackerRankProfile: yup.string(),
      HackerEarthProfile: yup.string(),
      LinkedInProfile: yup.string(),
      ContributionLink: yup.string(),
      LinkDescription: yup.string(),
    });
  }, [Translation, minMobileLength]) 

  const {
    dirty,
    values,
    setValues,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    resetForm,
    touched,
    submitCount,
    setFieldTouched,
    setFieldValue,
  } = useFormik({
    initialValues: profileInfo,
    enableReinitialize: true,
    onSubmit: (data) => {
      console.log("data", data);
      const profileObj = {
        FirstName: data.FirstName,
        LastName: data.LastName,
        GenderID: data.GenderID,
        CountryCodeID: data.CountryCodeID,
        Mobile: data.Mobile,
        Email: data.Email,
        StreetAddress: data.StreetAddress,
        Locality: data.Locality,
        Landmark: data.Landmark,
        CityID: data.CityID,
        StateID: data.StateID,
        CountryID: data.CountryID,
        TimezoneID: data.TimezoneID,
        TShirtID: data.TShirtID,
        InterviewBitProfile: data.InterviewBitProfile,
        CodeForceProfile: data.CodeForceProfile,
        CodeChefProfile: data.CodeChefProfile,
        HackerRankProfile: data.HackerRankProfile,
        HackerEarthProfile: data.HackerEarthProfile,
        LinkedInProfile: data.LinkedInProfile,
        ContributionLink: data.ContributionLink,
        LinkDescription: data.LinkDescription
      }
      dispatch(updateProfile(profileObj));
      setSelectedTab("academic");
    },
    validationSchema: Schema,
  });

  useEffect(() => {
    if (Common?.Country) {
      const country = Common?.Country?.find((item) => item.ID == profileInfo.CountryCodeID)
      setFieldValue("DialCode", country?.DialCode)
      setMinMobileLength(country?.NumbersLimit)
    }
  }, [Common, profileInfo?.CountryCodeID])

  useEffect(() => {
    if (Object.keys(profileInfo).length) {
      setValues({
        ...profileInfo,
        Landmark: profileInfo.Landmark || "",
        InterviewBitProfile: profileInfo.InterviewBitProfile || "",
        CodeForceProfile: profileInfo.CodeForceProfile || "",
        CodeChefProfile: profileInfo.CodeChefProfile || "",
        HackerRankProfile: profileInfo.HackerRankProfile || "",
        HackerEarthProfile: profileInfo.HackerEarthProfile || "",
        LinkedInProfile: profileInfo.LinkedInProfile || "",
        ContributionLink: profileInfo.ContributionLink || "",
        LinkDescription: profileInfo.LinkDescription || ""
      });
    }

  }, [profileInfo])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <Grid2 sx={styles}>
        <Typography fontWeight={500} variant="title">
          {Translation?.PersonalDetails}
        </Typography>
        <Grid2
          container
          spacing={2}
          columns={{ xl: 10, lg: 8, md: 6, sm: 4, xs: 2 }}
        >
           <Grid2 size={2}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" required label>
                {Translation?.FirstName}
              </FormLabel>
              <TextField
                error={touched.FirstName && errors.FirstName}
                helperText={touched.FirstName && errors.FirstName && errors.FirstName}
                onChange={handleChange}
                onBlur={handleBlur}
                name="FirstName"
                size="small"
                value={values.FirstName}
                placeholder={Translation?.EnterYourName}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" required label>
                {Translation?.LastName}
              </FormLabel>
              <TextField
                error={touched.LastName && errors.LastName}
                helperText={touched.LastName && errors.LastName && errors.LastName}
                onChange={handleChange}
                onBlur={handleBlur}
                name="LastName"
                size="small"
                value={values.LastName}
                placeholder={Translation?.EnterYourName}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <GenderSelect
              label={Translation?.Gender}
              value={values?.GenderID}
              error={
                touched.GenderID &&
                errors.GenderID &&
                errors.GenderID
              }
              onBlur={handleBlur}
              onChange={(value) => setFieldValue("GenderID", value)}
            />
          </Grid2>
          <Grid2 size={2}>
            <PhoneNumberInput
              label={Translation?.PhoneNumber}
              value={values.Mobile}
              min={minMobileLength}
              dialCodeValue={values.DialCode}
              handleBlur={handleBlur}
              handleChange={handleChange}
              onBlur={handleBlur}
              onChange={(value) => {
                setMinMobileLength(value?.NumbersLimit)
                setFieldValue("CountryCodeID", value?.ID)
                setFieldValue("DialCode", value?.DialCode)
              }}
              error={touched.Mobile && errors.Mobile && errors.Mobile}
              disabled={true}
            />
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" required label>
                {Translation?.EmailAddress}
              </FormLabel>
              <TextField
                error={touched.Email && errors.Email}
                helperText={touched.Email && errors.Email && errors.Email}
                // onChange={handleChange}
                // onBlur={handleBlur}
                name="Email"
                size="small"
                value={values.Email}
                placeholder={Translation?.EnterYourContactNumber}
                disabled={true}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <PincodeSelect
              value={values?.Pincode}
              error={touched.Pincode && errors.Pincode && errors.Pincode}
              onBlur={handleBlur}
              onChange={(value) => {
                setFieldValue("Pincode", value?.Pincode)
                setFieldValue("CityID", value?.ID)
                setFieldValue("StateID", value?.StateID)
                setFieldValue("CountryID", value?.CountryID)
              }}
            />
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" required label>
                {Translation?.StreetAddress}
              </FormLabel>
              <TextField
                error={touched.StreetAddress && errors.StreetAddress}
                helperText={touched.StreetAddress && errors.StreetAddress && errors.StreetAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                name="StreetAddress"
                size="small"
                value={values.StreetAddress}
                placeholder={Translation?.EnterStreetAddress}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <FormControl fullWidth>
              <FormLabel sx={{ height: "20px" }} required color="secondary" label>
                {Translation?.Locality}
              </FormLabel>
              <TextField
                error={touched.Locality && errors.Locality}
                helperText={touched.Locality && errors.Locality && errors.Locality}
                onChange={handleChange}
                onBlur={handleBlur}
                name="Locality"
                size="small"
                value={values.Locality}
                placeholder={Translation?.EnterLocality}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" label>
                {Translation?.Landmark}
              </FormLabel>
              <TextField
                error={touched.Landmark && errors.Landmark}
                helperText={touched.Landmark && errors.Landmark && errors.Landmark}
                onChange={handleChange}
                onBlur={handleBlur}
                name="Landmark"
                size="small"
                value={values.Landmark}
                placeholder={Translation?.EnterLandmark}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <CitySelect
              value={values?.CityID}
              error={(touched.CityID || submitCount > 0) && errors.CityID}
              disabled={true}
              onBlur={handleBlur}
              onChange={(value) => {
                setFieldValue("CityID", value?.ID)
                setFieldValue("StateID", value?.StateID)
                setFieldValue("CountryID", value?.CountryID)
              }}
            />
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <StateSelect
              value={values?.StateID}
              error={(touched.StateID || submitCount > 0) && errors.StateID}
              disabled={true}
              onBlur={handleBlur}
              onChange={(value) => {
                setFieldValue("StateID", value?.ID)
                setFieldValue("CountryID", value?.CountryID)
              }}
            />
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" required label>
                {Translation?.SettingsCountry}
              </FormLabel>
              <CountrySelect
                value={values?.CountryID}
                error={(touched.CountryID || submitCount > 0) && errors.CountryID}
                disabled={true}
                onBlur={() => setFieldTouched("CountryID", true)}
                onChange={(value) => setFieldValue("CountryID", value?.ID)}
                type="country"
              />
            </FormControl>
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <TimezoneSelect
              value={values?.TimezoneID}
              error={(touched.TimezoneID || submitCount > 0) && errors.TimezoneID}
              onBlur={handleBlur}
              onChange={(value) => setFieldValue("TimezoneID", value?.ID)}
            />
          </Grid2>
          <Grid2 size={2}>
            {" "}
            <TShirtSizeSelect
              value={values?.TShirtID}
              error={(touched.TShirtID || submitCount > 0) && errors.TShirtID}
              onBlur={handleBlur}
              onChange={(value) => setFieldValue("TShirtID", value?.ID)}
            />
          </Grid2>
        </Grid2>
        <Typography fontWeight={500} variant="title">
          {Translation?.SocialProfile}
        </Typography>
        <Grid2
          container
          spacing={2}
          columns={{ xl: 12, lg: 12, md: 8, sm: 8, xs: 4 }}
        >
          <Grid2 size={4}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" label>
                {Translation?.InterviewBitProfile}
              </FormLabel>
              <TextField
                onChange={handleChange}
                onBlur={handleBlur}
                name="InterviewBitProfile"
                size="small"
                value={values.InterviewBitProfile}
                placeholder={Translation?.AddInterviewBitProfileLink}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={4}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" label>
                {Translation?.CodeforcesProfile}
              </FormLabel>
              <TextField
                onChange={handleChange}
                onBlur={handleBlur}
                name="CodeForceProfile"
                size="small"
                value={values.CodeForceProfile}
                placeholder={Translation?.AddCodeforcesProfileLink}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={4}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" label>
                {Translation?.CodeChefProfile}
              </FormLabel>
              <TextField
                onChange={handleChange}
                onBlur={handleBlur}
                name="CodeChefProfile"
                size="small"
                value={values.CodeChefProfile}
                placeholder={Translation?.AddCodeChefProfileLink}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={4}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" label>
                {Translation?.HackerRankProfile}
              </FormLabel>
              <TextField
                onChange={handleChange}
                onBlur={handleBlur}
                name="HackerRankProfile"
                size="small"
                value={values.HackerRankProfile}
                placeholder={Translation?.AddHackerRankProfileLink}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={4}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" label>
                {Translation?.HackerEarthProfile}
              </FormLabel>
              <TextField
                onChange={handleChange}
                onBlur={handleBlur}
                name="HackerEarthProfile"
                size="small"
                value={values.HackerEarthProfile}
                placeholder={Translation?.AddHackerEarthProfileLink}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={4}>
            {" "}
            <FormControl fullWidth>
              <FormLabel color="secondary" label>
                {Translation?.LinkedInProfile}
              </FormLabel>
              <TextField
                onChange={handleChange}
                onBlur={handleBlur}
                name="LinkedInProfile"
                helperText={Translation?.LinkedInProfileHelperText}
                size="small"
                value={values.LinkedInProfile}
                placeholder={Translation?.AddLinkedInProfileLink}
              />
            </FormControl>
          </Grid2>
        </Grid2>
        <Typography fontWeight={500} variant="title">
          {Translation?.HighlightContribution}
        </Typography>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            {" "}
            <FormControl fullWidth>
              <TextField
                onChange={handleChange}
                onBlur={handleBlur}
                name="ContributionLink"
                size="small"
                value={values.ContributionLink}
                placeholder={Translation?.AddLink}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
            {" "}
            <FormControl fullWidth>
              <TextField
                onChange={handleChange}
                onBlur={handleBlur}
                name="LinkDescription"
                size="small"
                multiline
                rows={3}
                value={values.LinkDescription}
                placeholder={Translation?.LinkDescription}
              />
            </FormControl>
          </Grid2>
        </Grid2>
        <Button type="submit" variant="contained">
          {Translation?.UpdateDetails}
        </Button>
      </Grid2>
    </form>
  );
};

export default AccountDetails;
